// @flow

import R from 'ramda';

import { STANDARD_DATASOURCES } from '../../../common/support/eo_mapping/datasources';
import { filterMatchesEvent } from '../../../common/support/utils/druid/events';
import type {
  DruidResponse,
  DataSourceEnum,
  DruidTileData,
  DruidTile,
  CustomizableTileProps,
  EventDescription,
  Metric
} from '../../types/druidTypes';

export function _reduceResults(data: any[], metric: string): number {
  if (R.find(x => x.event, data)) {
    // groupby data must be handled differently than topn
    return data.reduce((acc, curr) => {
      acc += R.pathOr(0, ['event', metric], curr);
      return acc;
    }, 0);
  }
  return R.path([0, 'result', 0, metric], data);
}

export function _getFractionFromDruidSources(
  data: DruidResponse,
  numeratorSource?: DataSourceEnum,
  denominatorSource?: DataSourceEnum,
  metric: string
): ?number {
  if (!numeratorSource || !denominatorSource) {
    return;
  }
  const numerator = _reduceResults(data[numeratorSource], metric);
  const denominator = _reduceResults(data[denominatorSource], metric);
  if (typeof numerator !== 'number' || typeof denominator !== 'number') {
    return;
  }
  return numerator / denominator;
}

export function _getTileValue(
  tile: DruidTileData,
  data: ?DruidResponse,
  unique: ?boolean
): ?number {
  const { source, numerator, denominator, transformer } = tile;

  const metric = unique ? 'unique_count' : 'total_count';

  let current;

  if (!data) {
    return;
  }

  if (source) {
    // render the total for a given datasource
    current = _reduceResults(data[source], metric);
  } else if (transformer) {
    // used passed in transformer to calculate value
    current = transformer(data, metric);
  } else {
    // use passed in numerator and denominator to calculate value
    current = _getFractionFromDruidSources(
      data,
      numerator,
      denominator,
      metric
    );
  }

  return current;
}

export function _getCustomizableTileValue(
  tile: DruidTileData,
  data: ?DruidResponse,
  unique: ?boolean
): ?number {
  const { source } = tile;
  const metric = unique ? 'unique_count' : 'total_count';

  if (!data || !source) {
    return;
  }

  // when source is not a core datasource, filter data so that only the requested event (i.e. ADD_TO_CART) is reflected
  if (Object.keys(STANDARD_DATASOURCES).includes(source)) {
    const filteredData = data[source].filter(
      result => result.event && result.event.name === tile.title
    );
    return _reduceResults(filteredData, metric);
  }

  return _reduceResults(data[source], metric);
}

export function hydrateTiles(
  tileData: DruidTileData[],
  data?: ?DruidResponse,
  prevData?: ?DruidResponse,
  unique?: boolean,
  loading?: boolean
): Array<DruidTile> {
  return tileData.map((tile, i) => {
    if (loading) {
      return {
        title: tile.title,
        key: tile.key,
        description: tile.description
      };
    }

    let current = _getTileValue(tile, data, unique);
    if (typeof current !== 'number') {
      current = '-';
    }
    let previous = _getTileValue(tile, prevData, unique);
    if (typeof previous !== 'number') {
      previous = '-';
    }

    return {
      title: tile.title,
      key: tile.key,
      description: tile.description,
      oppositeArrow: tile.oppositeArrow,
      formatter: tile.formatter,
      current,
      previous
    };
  });
}

export function hydrateCustomizableTile(
  tileProps: CustomizableTileProps
): DruidTile {
  const { tileData, data, prevData, unique, loading } = tileProps;
  if (loading) {
    return {
      title: tileData.title,
      key: tileData.key,
      description: tileData.description
    };
  }

  let current = _getCustomizableTileValue(tileData, data, unique);
  if (typeof current !== 'number') {
    current = '-';
  }
  let previous = _getCustomizableTileValue(tileData, prevData, unique);
  if (typeof previous !== 'number') {
    previous = '-';
  }

  return {
    title: tileData.title,
    key: tileData.key,
    description: tileData.description,
    oppositeArrow: tileData.oppositeArrow,
    formatter: tileData.formatter,
    current,
    previous
  };
}

export function generateTransformerFromEventDescription(
  eventDescription: EventDescription
): (data: DruidResponse, metric: Metric) => ?number {
  function transformer(data: DruidResponse, metric: Metric): ?number {
    if (!data[eventDescription.dataSource]) {
      return;
    }
    return data[eventDescription.dataSource].reduce((acc, record) => {
      const { event } = record;
      if (filterMatchesEvent(eventDescription.filter, event)) {
        return acc + event[metric];
      }
      return acc;
    }, 0);
  }
  return transformer;
}
